import React, { useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SxProps, Theme } from '@mui/material';
import { format, isValid, parse, subYears } from 'date-fns';
import { ar, enUS, es, fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/**
 * Date of birth field.
 *
 * The native <input type="date"> renders the browser's own calendar, which ignores
 * the app theme and opens on the current month (useless for a birth date). This
 * wraps the MUI date picker instead: year view first, localised, no future date.
 *
 * The value is kept in the backend format (yyyy-MM-dd) while the user sees dd/MM/yyyy.
 */

const BACKEND_FORMAT = 'yyyy-MM-dd';
const MIN_DATE = new Date(1900, 0, 1);

const LOCALES: Record<string, Locale> = { fr, en: enUS, es, ar };

export const parseBirthdate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = parse(value, BACKEND_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

export const formatBirthdate = (date: Date): string => format(date, BACKEND_FORMAT);

interface BirthdatePickerProps {
  value?: string | null;
  /** Receives the date in backend format (yyyy-MM-dd), or '' when cleared. */
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  /** Force the calendar popup to the dark palette, for surfaces that are dark whatever the app theme. */
  dark?: boolean;
}

const BirthdatePicker: React.FC<BirthdatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  size,
  sx,
  dark = false,
}) => {
  const { i18n } = useTranslation();
  const adapterLocale = LOCALES[i18n.language?.split('-')[0] ?? 'en'] ?? enUS;

  const parsedValue = useMemo(() => parseBirthdate(value), [value]);
  // Empty field: start the year list around a plausible birth year instead of today.
  const referenceDate = useMemo(() => subYears(new Date(), 30), []);

  const darkPaperSx: SxProps<Theme> = {
    backgroundColor: '#1E2025',
    backgroundImage: 'none',
    color: '#FFFFFF',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    '& .MuiPickersCalendarHeader-label, & .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton, & .MuiDayCalendar-weekDayLabel':
      { color: '#FFFFFF' },
    '& .MuiDayCalendar-weekDayLabel': { color: '#8E8E93' },
    '& .MuiPickersDay-root, & .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
      color: '#FFFFFF',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
      '&.Mui-selected': {
        backgroundColor: '#354EEF',
        color: '#FFFFFF',
        '&:hover, &:focus': { backgroundColor: '#354EEF' },
      },
      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <DatePicker
        value={parsedValue}
        onChange={(date) => {
          if (date && isValid(date)) onChange(formatBirthdate(date));
          else if (date === null) onChange('');
        }}
        label={label}
        format="dd/MM/yyyy"
        openTo="year"
        views={['year', 'month', 'day']}
        minDate={MIN_DATE}
        disableFuture
        referenceDate={referenceDate}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth,
            size,
            placeholder,
            error,
            helperText,
            sx,
          },
          ...(dark
            ? {
                desktopPaper: { sx: darkPaperSx },
                mobilePaper: { sx: darkPaperSx },
              }
            : {}),
        }}
      />
    </LocalizationProvider>
  );
};

export default BirthdatePicker;
